(() => {
  if (window.__roadSpecOrderTrackingLoaded) return;
  window.__roadSpecOrderTrackingLoaded = true;

  const page = document.querySelector('[data-rsp-track-page]');
  if (!page) return;

  const form = page.querySelector('[data-rsp-track-form]');
  const identifierField = page.querySelector('#RoadSpecTrackIdentifier');
  const emailField = page.querySelector('#RoadSpecTrackEmail');
  const carrierField = page.querySelector('#RoadSpecTrackCarrier');
  const message = page.querySelector('[data-rsp-track-form-message]');
  const emptyState = page.querySelector('[data-rsp-track-empty]');
  const result = page.querySelector('[data-rsp-track-result]');
  const resultTitle = page.querySelector('[data-rsp-track-result-title]');
  const carrierLabel = page.querySelector('[data-rsp-track-carrier-label]');
  const stateLabel = page.querySelector('[data-rsp-track-state]');
  const summary = page.querySelector('[data-rsp-track-summary]');
  const facts = page.querySelector('[data-rsp-track-facts]');
  const timeline = page.querySelector('[data-rsp-track-timeline]');
  const carrierLink = page.querySelector('[data-rsp-track-carrier-link]');
  const endpoint = page.dataset.rspTrackingEndpoint?.trim() || '';

  const track = (eventName, payload = {}) => {
    try { window.Shopify?.analytics?.publish(eventName, payload); } catch (_) {}
  };

  const normalize = (value) => String(value || '').trim().replace(/\s+/g, '').toUpperCase();
  const pretty = (value) => String(value || '').replace(/\b\w/g, (letter) => letter.toUpperCase());

  const carrierNames = { ups: 'UPS', usps: 'USPS', fedex: 'FedEx', dhl: 'DHL', other: 'Carrier' };
  const carrierUrl = (carrier, identifier) => {
    const encoded = encodeURIComponent(identifier);
    const urls = {
      ups: `https://www.ups.com/track?tracknum=${encoded}`,
      usps: `https://tools.usps.com/go/TrackConfirmAction?tRef=fullpage&tLc=2&text28777=&tLabels=${encoded}`,
      fedex: `https://www.fedex.com/fedextrack/?trknbr=${encoded}`,
      dhl: `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${encoded}`,
      other: `https://www.17track.net/en?nums=${encoded}`,
    };
    return urls[carrier] || urls.other;
  };

  const detectCarrier = (identifier, selected) => {
    if (selected && selected !== 'auto') return selected;
    if (/^1Z[0-9A-Z]{16}$/.test(identifier)) return 'ups';
    if (/^(?:9[2345]|7\d)\d{18,22}$/.test(identifier)) return 'usps';
    if (/^\d{12,15}$/.test(identifier)) return 'fedex';
    if (/^(?:JD|GM|LX|RX|CN)[A-Z0-9]{8,18}$/.test(identifier)) return 'dhl';
    return 'other';
  };

  const looksLikeOrderNumber = (identifier) => /^#?\d{1,10}$/.test(identifier) || /^#?(?:RSP|RS)-?[A-Z0-9-]{3,}$/i.test(identifier);

  const setMessage = (text) => {
    message.textContent = text;
    message.hidden = !text;
  };

  const showFacts = (items) => {
    facts.replaceChildren();
    const entries = Object.entries(items).filter(([, value]) => value);
    entries.forEach(([label, value]) => {
      const wrapper = document.createElement('div');
      const term = document.createElement('dt');
      const detail = document.createElement('dd');
      term.textContent = label;
      detail.textContent = value;
      wrapper.append(term, detail);
      facts.append(wrapper);
    });
    facts.hidden = entries.length === 0;
  };

  const showTimeline = (items) => {
    timeline.replaceChildren();
    items.forEach((item, index) => {
      const row = document.createElement('li');
      if (item.complete) row.classList.add('is-complete');
      if (item.current) row.classList.add('is-current');
      const marker = document.createElement('span');
      marker.className = 'rsp-track-timeline__marker';
      marker.textContent = item.complete ? '✓' : String(index + 1).padStart(2, '0');
      marker.setAttribute('aria-hidden', 'true');
      const copy = document.createElement('span');
      copy.className = 'rsp-track-timeline__copy';
      const title = document.createElement('strong');
      const detail = document.createElement('span');
      title.textContent = item.title;
      detail.textContent = item.detail || '';
      copy.append(title, detail);
      row.append(marker, copy);
      timeline.append(row);
    });
  };

  const showResult = ({ carrier, state, title, summaryText, facts: factItems, timeline: timelineItems, link }) => {
    emptyState.hidden = true;
    result.hidden = false;
    carrierLabel.textContent = carrier || 'RoadSpec delivery';
    stateLabel.textContent = state || 'Update';
    resultTitle.textContent = title || 'Delivery update';
    summary.textContent = summaryText || '';
    showFacts(factItems || {});
    showTimeline(timelineItems || []);
    if (link) {
      carrierLink.href = link;
      carrierLink.hidden = false;
    } else {
      carrierLink.hidden = true;
      carrierLink.removeAttribute('href');
    }
  };

  const showCarrierHandoff = (identifier, carrier) => {
    const name = carrierNames[carrier] || carrierNames.other;
    showResult({
      carrier: name,
      state: 'Carrier handoff',
      title: 'Tracking number recognized',
      summaryText: `Your ${name} tracking number is ready. Live scan events remain with the carrier, so open the secure carrier tracker for the latest movement and delivery estimate.`,
      facts: { 'Tracking number': identifier, Carrier: name },
      timeline: [
        { title: 'Tracking number submitted', detail: 'RoadSpec received the number you entered.', complete: true },
        { title: 'Carrier scan updates', detail: 'Open carrier tracking to see the latest scan.', current: true },
        { title: 'Delivered', detail: 'The carrier will confirm delivery when complete.' },
      ],
      link: carrierUrl(carrier, identifier),
    });
  };

  const showOrderFallback = (identifier) => {
    showResult({
      carrier: 'RoadSpec order',
      state: 'Secure lookup',
      title: 'Order status link needed',
      summaryText: `We received order ${identifier}. For privacy, order details must be retrieved through Shopify’s secure order-status link or a verified tracking number. Check your confirmation email or contact RoadSpec with the order number and checkout email.`,
      facts: { 'Order number': identifier, 'Lookup protection': 'Email verification required' },
      timeline: [
        { title: 'Order number submitted', detail: 'RoadSpec received the number you entered.', complete: true },
        { title: 'Secure order verification', detail: 'Use the order-status link from your confirmation email.', current: true },
        { title: 'Delivery progress', detail: 'Shipment updates appear once a verified tracking number is available.' },
      ],
    });
  };

  const renderRemote = (payload, identifier, carrier) => {
    const events = Array.isArray(payload.events) ? payload.events : [];
    const status = payload.statusLabel || payload.status || 'Update available';
    const remoteCarrier = payload.carrier || carrierNames[carrier] || 'RoadSpec delivery';
    const remoteLink = payload.trackingUrl || payload.carrierUrl || (carrier !== 'other' ? carrierUrl(carrier, identifier) : '');
    showResult({
      carrier: remoteCarrier,
      state: status,
      title: payload.title || 'Delivery progress',
      summaryText: payload.summary || 'The latest available fulfillment update is shown below.',
      facts: { 'Tracking number': payload.trackingNumber || identifier, Carrier: remoteCarrier, 'Estimated delivery': payload.estimatedDelivery },
      timeline: events.length ? events.map((event) => ({ title: event.title || event.label || 'Shipment update', detail: [event.timestamp, event.location, event.detail].filter(Boolean).join(' · '), complete: Boolean(event.complete), current: Boolean(event.current) })) : [{ title: status, detail: 'Latest fulfillment status received.', current: true }],
      link: remoteLink,
    });
  };

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    setMessage('');
    const identifier = normalize(identifierField.value);
    const email = emailField.value.trim();
    if (!identifier) {
      setMessage('Enter an order number or tracking number to continue.');
      identifierField.focus();
      return;
    }
    if (looksLikeOrderNumber(identifier) && !email) {
      setMessage('Enter the email used at checkout for a secure order-number lookup.');
      emailField.focus();
      return;
    }
    if (email && !emailField.checkValidity()) {
      setMessage('Check the checkout email address and try again.');
      emailField.focus();
      return;
    }

    const carrier = detectCarrier(identifier, carrierField.value);
    track('roadspec_order_tracking_submitted', { identifier_type: looksLikeOrderNumber(identifier) ? 'order' : 'tracking', carrier });
    if (!endpoint) {
      if (looksLikeOrderNumber(identifier)) showOrderFallback(identifier);
      else showCarrierHandoff(identifier, carrier);
      return;
    }

    const button = form.querySelector('button[type="submit"]');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.firstChild.textContent = 'Checking delivery progress ';
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ identifier, email: email || null, carrier }),
      });
      if (!response.ok) throw new Error('Tracking request failed');
      renderRemote(await response.json(), identifier, carrier);
      track('roadspec_order_tracking_loaded', { identifier_type: looksLikeOrderNumber(identifier), carrier });
    } catch (_) {
      setMessage('We could not retrieve a live update right now. Check the number or contact RoadSpec for help.');
      track('roadspec_order_tracking_failed', { identifier_type: looksLikeOrderNumber(identifier), carrier });
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.firstChild.textContent = 'Show delivery progress ';
    }
  });
})();
